import { useMemo, useState } from 'react';
import { SvgIcon } from '../../components/icons';
import {
  ACTIVITY,
  APPROVALS,
  CLASSIFICATIONS,
  STATUS_COLOR,
  STATUSES,
  TAILS,
  usd,
  WARRANTY_CLAIMS,
  WarrantyClaim,
  sumBy,
} from '../data/warrantyDemo';
import type { Column } from '../primitives';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  DataTable,
  Dialog,
  EmptyState,
  IconButton,
  Input,
  KpiCard,
  Radio,
  SearchField,
  SegmentedControl,
  Select,
  StatusChip,
  Switch,
  TabPanel,
  Tabs,
  Textarea,
  useTabsId,
  Tooltip,
  useToast,
} from '../primitives';

const TABS = ['Claims', 'Approvals', 'Activity', 'Archive'];

/**
 * The demo screen, modelled on the real Warranty Claims page.
 *
 * Chosen because it is the densest screen in the app: a 16-column sortable
 * table with a totals row, money formatting, status + classification, filter
 * bar and a detail dialog. If a design direction survives this screen it will
 * survive the rest.
 *
 * This component is theme-agnostic — it is rendered identically under all five
 * directions, which is what makes the side-by-side comparison honest.
 */
export const WarrantyDemo = () => {
  const toast = useToast();

  const tabsId = useTabsId();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [tail, setTail] = useState('');
  const [status, setStatus] = useState('');
  const [classification, setClassification] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [view, setView] = useState<'all' | 'mine'>('all');

  const [selected, setSelected] = useState<WarrantyClaim | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return WARRANTY_CLAIMS.filter((r) => {
      if (tail && r.tail_number !== tail) return false;
      if (status && r.warranty_status !== status) return false;
      if (classification && r.claim_classification_type !== classification)
        return false;
      if (onlyOpen && r.warranty_status !== 'In process') return false;
      if (view === 'mine' && r.owner !== 'Sigrún H.') return false;
      if (!q) return true;
      return (
        r.tail_number.toLowerCase().includes(q) ||
        r.fault_barcode.toLowerCase().includes(q) ||
        r.cage_code_text.toLowerCase().includes(q) ||
        r.last_comment.toLowerCase().includes(q) ||
        r.po_number.toLowerCase().includes(q)
      );
    });
  }, [search, tail, status, classification, onlyOpen, view]);

  const clearFilters = () => {
    setSearch('');
    setTail('');
    setStatus('');
    setClassification('');
    setOnlyOpen(false);
    setView('all');
  };

  const hasFilters =
    Boolean(search || tail || status || classification) ||
    onlyOpen ||
    view !== 'all';

  const columns: Array<Column<WarrantyClaim>> = [
    {
      key: 'tail_number',
      header: 'Tail',
      sortable: true,
      sticky: true,
      render: (r) => <span className="ds-mono">{r.tail_number}</span>,
      total: 'Total',
    },
    {
      key: 'created_date_text',
      header: 'Created',
      sortable: true,
      priority: 'medium',
      render: (r) => <span className="ds-num">{r.created_date_text}</span>,
    },
    {
      key: 'fault_barcode',
      header: 'MXI',
      priority: 'low',
      render: (r) => (
        <button
          type="button"
          className="ds-mono"
          style={{ color: 'var(--ds-accent-text)', textDecoration: 'underline' }}
          onClick={() => toast.push({ title: `Would open ${r.fault_barcode} in MXI` })}
        >
          {r.fault_barcode}
        </button>
      ),
    },
    {
      key: 'cage_code_text',
      header: 'Cage Code',
      sortable: true,
      priority: 'medium',
      render: (r) => r.cage_code_text,
    },
    {
      key: 'claim_classification_type',
      header: 'Classification',
      sortable: true,
      render: (r) => <Chip>{r.claim_classification_type}</Chip>,
    },
    {
      key: 'last_comment',
      header: 'Last comment',
      priority: 'low',
      render: (r) => (
        <span
          className="block truncate ds-muted"
          style={{ maxWidth: 260 }}
          title={r.last_comment}
        >
          {r.last_comment}
        </span>
      ),
    },
    {
      key: 'po_number',
      header: 'PO / Task',
      priority: 'low',
      render: (r) => <span className="ds-mono">{r.po_number}</span>,
    },
    {
      key: 'part_count',
      header: 'Parts',
      sortable: true,
      align: 'right',
      priority: 'medium',
      render: (r) => r.part_count,
      total: String(sumBy(rows, 'part_count')),
    },
    {
      key: 'part_claimed_cost',
      header: 'Parts $',
      sortable: true,
      align: 'right',
      render: (r) => usd.format(r.part_claimed_cost),
      total: usd.format(sumBy(rows, 'part_claimed_cost')),
    },
    {
      key: 'manhours_claimed_cost',
      header: 'Manhours $',
      sortable: true,
      align: 'right',
      priority: 'low',
      render: (r) => usd.format(r.manhours_claimed_cost),
      total: usd.format(sumBy(rows, 'manhours_claimed_cost')),
    },
    {
      key: 'shipping_claimed_cost',
      header: 'Shipping $',
      sortable: true,
      align: 'right',
      priority: 'low',
      render: (r) => usd.format(r.shipping_claimed_cost),
      total: usd.format(sumBy(rows, 'shipping_claimed_cost')),
    },
    {
      key: 'total_claimed_cost',
      header: 'Total',
      sortable: true,
      align: 'right',
      render: (r) => (
        <strong className="ds-num">{usd.format(r.total_claimed_cost)}</strong>
      ),
      total: usd.format(sumBy(rows, 'total_claimed_cost')),
    },
    {
      key: 'warranty_status',
      header: 'Status',
      sortable: true,
      render: (r) => (
        // The hue here is fixed by the domain and passed through untouched;
        // StatusChip renders it as a dot beside a text label, so the colour is
        // never the only signal and never fights the theme for contrast.
        <StatusChip
          color={STATUS_COLOR[r.warranty_status]}
          label={r.warranty_status}
        />
      ),
    },
    {
      key: 'claim_request_type',
      header: 'Result',
      priority: 'medium',
      render: (r) =>
        r.claim_request_type ? (
          <Chip tone="ok">{r.claim_request_type}</Chip>
        ) : (
          <span className="ds-muted">—</span>
        ),
    },
    {
      key: 'has_attachment',
      header: '',
      render: (r) =>
        r.has_attachment ? (
          <Tooltip label="Has attachment">
            <span className="ds-muted inline-flex">
              <SvgIcon name="attachment" size={14} />
            </span>
          </Tooltip>
        ) : null,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        // A focusable button, not an onClick on the <tr> — the production
        // tables put the click on the row itself, which no keyboard can reach.
        <IconButton
          label={`Open claim ${r.warranty_id}`}
          size="sm"
          onClick={() => setSelected(r)}
        >
          <SvgIcon name="search" size={14} />
        </IconButton>
      ),
    },
  ];

  const openCount = WARRANTY_CLAIMS.filter(
    (r) => r.warranty_status === 'In process',
  ).length;

  return (
    <>
      <div className="ds-page-header">
        <div>
          <h1 className="ds-title">Warranty Claims</h1>
          <p className="ds-subtitle">
            {rows.length} of {WARRANTY_CLAIMS.length} claims · KEF Technical
            Services
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            startIcon={<SvgIcon name="download" size={14} />}
            onClick={() =>
              toast.push({
                title: 'Export started',
                body: `${rows.length} rows queued as CSV.`,
                tone: 'info',
              })
            }
          >
            Export
          </Button>
          <Button
            variant="primary"
            startIcon={<SvgIcon name="add" size={14} />}
            onClick={() => setSelected(WARRANTY_CLAIMS[0])}
          >
            New claim
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="flex flex-wrap gap-3" style={{ marginBottom: 16 }}>
        <div className="min-w-0 flex-1" style={{ minWidth: 170 }}>
          <KpiCard
            label="Open claims"
            value={String(openCount)}
            delta="+3 this week"
            direction="up"
            icon={<SvgIcon name="hourglassFull" size={16} />}
          />
        </div>
        <div className="min-w-0 flex-1" style={{ minWidth: 170 }}>
          <KpiCard
            label="Claimed YTD"
            value={usd.format(sumBy(WARRANTY_CLAIMS, 'total_claimed_cost'))}
            delta="+8.4% vs 2025"
            direction="up"
            icon={<SvgIcon name="attachMoney" size={16} />}
          />
        </div>
        <div className="min-w-0 flex-1" style={{ minWidth: 170 }}>
          <KpiCard
            label="Recovery rate"
            value="61%"
            delta="-2.1 pts"
            direction="down"
            icon={<SvgIcon name="checkCircleOutline" size={16} />}
          />
        </div>
        <div className="min-w-0 flex-1" style={{ minWidth: 170 }}>
          <KpiCard
            label="Avg. days to close"
            value="17.4"
            delta="unchanged"
            direction="flat"
            icon={<SvgIcon name="accessTime" size={16} />}
          />
        </div>
      </div>

      <Tabs
        tabs={TABS}
        value={tab}
        onChange={setTab}
        idBase={tabsId}
        className="mb-3"
      />

      {/* ── Claims ─────────────────────────────────────────────────────── */}
      <TabPanel baseId={tabsId} index={0} active={tab === 0}>
        <Card flush>
          <CardHeader>
            <div className="ds-toolbar">
              <SearchField
                placeholder="Tail, MXI, PO, comment…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search claims"
                wrapperClassName="w-full sm:w-auto"
              />
              <Select
                label="Tail"
                value={tail}
                onChange={(e) => setTail(e.target.value)}
                options={[
                  { value: '', label: 'All tails' },
                  ...TAILS.map((t) => ({ value: t, label: t })),
                ]}
              />
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={[
                  { value: '', label: 'Any status' },
                  ...STATUSES.map((s) => ({ value: s, label: s })),
                ]}
              />
              <Select
                label="Classification"
                value={classification}
                onChange={(e) => setClassification(e.target.value)}
                options={[
                  { value: '', label: 'Any classification' },
                  ...CLASSIFICATIONS.map((c) => ({ value: c, label: c })),
                ]}
              />
              <Checkbox
                label="Open only"
                checked={onlyOpen}
                onChange={(e) => setOnlyOpen(e.target.checked)}
                className="self-center"
              />
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </div>

            <SegmentedControl
              label="Claim scope"
              value={view}
              onChange={setView}
              options={[
                { value: 'all', label: 'All' },
                { value: 'mine', label: 'Mine' },
              ]}
            />
          </CardHeader>

          <DataTable
            caption="Warranty claims"
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.warranty_id}
            showTotals
            emptyTitle="No claims match these filters"
            emptyHint="Try widening the tail or status filter, or clear them all to start again."
            emptyAction={
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        </Card>
      </TabPanel>

      {/* ── Approvals: a second, different workflow shape ───────────────── */}
      <TabPanel baseId={tabsId} index={1} active={tab === 1}>
        <div className="flex flex-wrap gap-3">
          <div className="min-w-0 flex-1" style={{ minWidth: 320 }}>
            <Card>
              <CardHeader>
                <h2 className="ds-section-title">Awaiting your approval</h2>
                <Chip tone="warn">{APPROVALS.length} pending</Chip>
              </CardHeader>
              <CardBody>
                <ul className="flex flex-col gap-2">
                  {APPROVALS.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center gap-3"
                      style={{
                        padding: 10,
                        border: '1px solid var(--ds-border)',
                        borderRadius: 'var(--ds-r-md)',
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <div style={{ fontWeight: 600 }}>
                          Claim #{a.id}{' '}
                          <span className="ds-mono ds-muted">{a.tail}</span>
                        </div>
                        <div className="ds-muted" style={{ fontSize: 'var(--ds-fs-xs)' }}>
                          {usd.format(a.amount)} · {a.owner} · open {a.age}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          toast.push({
                            title: `Claim #${a.id} declined`,
                            tone: 'danger',
                          })
                        }
                      >
                        Decline
                      </Button>
                      <Button
                        variant="confirm"
                        size="sm"
                        onClick={() => setConfirmOpen(true)}
                      >
                        Approve
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>

          <div style={{ width: 300, flex: '0 1 300px' }}>
            <Card>
              <CardHeader>
                <h2 className="ds-section-title">Threshold</h2>
              </CardHeader>
              <CardBody>
                <p className="ds-muted" style={{ fontSize: 'var(--ds-fs-xs)' }}>
                  Claims above this value need a supervisor signature before
                  they can be submitted to the vendor.
                </p>
                <div style={{ marginTop: 12 }}>
                  <Input
                    label="Approval threshold (USD)"
                    defaultValue="50000"
                    inputMode="numeric"
                  />
                </div>
                <div style={{ marginTop: 12 }}>
                  <Switch label="Notify me by email" defaultChecked />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Switch label="Auto-approve pool items" />
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </TabPanel>

      {/* ── Activity feed ──────────────────────────────────────────────── */}
      <TabPanel baseId={tabsId} index={2} active={tab === 2}>
        <Card>
          <CardHeader>
            <h2 className="ds-section-title">Recent activity</h2>
            <Button variant="ghost" size="sm">
              View all
            </Button>
          </CardHeader>
          <CardBody>
            <ul className="flex flex-col">
              {ACTIVITY.map((a, i) => (
                <li
                  key={a.id}
                  className="flex items-center gap-3"
                  style={{
                    padding: '8px 0',
                    borderTop: i === 0 ? undefined : '1px solid var(--ds-border)',
                  }}
                >
                  <span className="ds-avatar" aria-hidden="true">
                    {a.who
                      .split(' ')
                      .map((p) => p[0])
                      .join('')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong>{a.who}</strong>{' '}
                    <span className="ds-muted">{a.what}</span>
                  </span>
                  <Chip tone={a.tone}>{a.when}</Chip>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </TabPanel>

      {/* ── Empty state ────────────────────────────────────────────────── */}
      <TabPanel baseId={tabsId} index={3} active={tab === 3}>
        <Card>
          <EmptyState
            icon={<SvgIcon name="folderDelete" size={20} />}
            title="No archived claims for 2026"
            hint="Claims are archived automatically twelve months after they are closed. Nothing has reached that age in the current period."
            action={
              <Button variant="secondary" size="sm">
                Browse 2025 archive
              </Button>
            }
          />
        </Card>
      </TabPanel>

      {/* ── Detail dialog with a full form ─────────────────────────────── */}
      <Dialog
        open={selected !== null}
        title={selected ? `Claim #${selected.warranty_id}` : ''}
        width="lg"
        onClose={() => setSelected(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSelected(null)}>
              Cancel
            </Button>
            <Button variant="secondary">Save draft</Button>
            {/* The confirmation action. Saga Gold renders this in the classic
                golden livery; every other theme uses its own accent. */}
            <Button
              variant="confirm"
              startIcon={<SvgIcon name="check" size={14} />}
              onClick={() => {
                setSelected(null);
                toast.push({
                  title: 'Claim submitted to vendor',
                  body: `#${selected?.warranty_id} · ${selected?.cage_code_text}`,
                  tone: 'ok',
                });
              }}
            >
              Submit claim
            </Button>
          </>
        }
      >
        {selected && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip
                color={STATUS_COLOR[selected.warranty_status]}
                label={selected.warranty_status}
              />
              <Chip>{selected.claim_classification_type}</Chip>
              <span className="ds-mono ds-muted">{selected.fault_barcode}</span>
            </div>

            <div className="flex flex-wrap gap-3">
              <Input
                label="Tail number"
                defaultValue={selected.tail_number}
                wrapperClassName="flex-1"
                style={{ minWidth: 140 }}
              />
              <Input
                label="PO / Task barcode"
                defaultValue={selected.po_number}
                wrapperClassName="flex-1"
                style={{ minWidth: 140 }}
              />
              <Select
                label="Classification"
                defaultValue={selected.claim_classification_type}
                wrapperClassName="flex-1"
                options={CLASSIFICATIONS.map((c) => ({ value: c, label: c }))}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Input
                label="Parts cost (USD)"
                defaultValue={String(selected.part_claimed_cost)}
                wrapperClassName="flex-1"
                inputMode="numeric"
              />
              <Input
                label="Manhours cost (USD)"
                defaultValue={String(selected.manhours_claimed_cost)}
                wrapperClassName="flex-1"
                inputMode="numeric"
              />
              <Input
                label="Shipping (USD)"
                defaultValue={String(selected.shipping_claimed_cost)}
                wrapperClassName="flex-1"
                inputMode="numeric"
                hint="Include customs where applicable"
              />
            </div>

            <Textarea
              label="Latest comment"
              defaultValue={selected.last_comment}
              rows={3}
            />

            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="ds-label">Expected result</legend>
              <div className="flex flex-wrap gap-4" style={{ marginTop: 2 }}>
                <Radio name="result" label="FOC spare" defaultChecked />
                <Radio name="result" label="FOC repair" />
                <Radio name="result" label="Credit" />
                <Radio name="result" label="Partial claim" />
              </div>
            </fieldset>

            <div className="flex flex-wrap gap-4">
              <Checkbox label="Attach teardown report" defaultChecked />
              <Checkbox label="Notify vendor on submit" />
              <Switch label="Urgent (AOG)" />
            </div>

            <Input
              label="Vendor reference"
              placeholder="Required before submission"
              error="Vendor reference is required for OEM Direct claims."
            />
          </div>
        )}
      </Dialog>

      {/* ── Destructive confirmation (strict: ESC/backdrop do not close) ── */}
      <Dialog
        open={confirmOpen}
        title="Approve claim above threshold?"
        width="sm"
        strict
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="confirm"
              onClick={() => {
                setConfirmOpen(false);
                toast.push({
                  title: 'Claim approved',
                  body: 'Supervisor signature recorded against the claim.',
                  tone: 'ok',
                });
              }}
            >
              Approve claim
            </Button>
          </>
        }
      >
        <p>
          This claim exceeds the {usd.format(50000)} approval threshold.
          Approving records your signature against it and submits it to the
          vendor. This cannot be undone from Maintener.
        </p>
      </Dialog>
    </>
  );
};
